import { BdcShipmentKind, BdcShipmentStatus } from "../../types/bdc.type";
import { TagTone } from "./styles";

/**
 * The four shipment statuses of plan 1.5 §2, in flow order:
 *   ร่าง → ยืนยัน DO → ออก INV แล้ว → รอนำส่ง 7-11
 * Colors follow the Queue board: grey, blue, amber, green.
 */
export const BDC_STATUS_ORDER: BdcShipmentStatus[] = ["draft", "do_confirmed", "invoiced", "queued_7_11"];

export const BDC_STATUS_META: Record<BdcShipmentStatus, { label: string; step: string; tone: TagTone }> = {
  draft: { label: "ร่าง", step: "ร่าง", tone: "mut" },
  do_confirmed: { label: "ยืนยัน DO", step: "ยืนยัน DO", tone: "info" },
  invoiced: { label: "ออก INV แล้ว", step: "ออก INV", tone: "warn" },
  queued_7_11: { label: "รอนำส่ง 7-11", step: "รอนำส่ง 7-11", tone: "ok" },
};

export const isBdcShipmentStatus = (value: unknown): value is BdcShipmentStatus =>
  typeof value === "string" && (BDC_STATUS_ORDER as string[]).includes(value);

/** Thai label; an unknown status (a newer API) shows as sent. */
export const statusLabel = (status: string | null | undefined): string =>
  isBdcShipmentStatus(status) ? BDC_STATUS_META[status].label : status || "-";

export const statusTone = (status: string | null | undefined): TagTone =>
  isBdcShipmentStatus(status) ? BDC_STATUS_META[status].tone : "mut";

/** 0-based place in the flow; -1 for an unknown status. */
export const statusStep = (status: string | null | undefined): number =>
  isBdcShipmentStatus(status) ? BDC_STATUS_ORDER.indexOf(status) : -1;

export interface ShipmentActions {
  /** ปรับยอดส่ง (Auto / Manual) — a draft with per-store lines. */
  canEditDelivery: boolean;
  canConfirmDo: boolean;
  canUnconfirmDo: boolean;
  canIssueInvoice: boolean;
  canCancelInvoice: boolean;
}

/**
 * What the status table of plan §2 allows. The server is the judge (409 otherwise); this only
 * decides which buttons are drawn. A kind-X PO (no PICKLST) has no per-store lines to adjust.
 */
export const shipmentActions = (status: string | null | undefined, kind?: BdcShipmentKind): ShipmentActions => ({
  canEditDelivery: status === "draft" && kind !== "X",
  canConfirmDo: status === "draft",
  canUnconfirmDo: status === "do_confirmed",
  canIssueInvoice: status === "do_confirmed",
  canCancelInvoice: status === "invoiced",
});

/** Rows per status, every status present (0 when none). */
export const countByStatus = (rows: { status: string }[]): Record<BdcShipmentStatus, number> => {
  const counts: Record<BdcShipmentStatus, number> = { draft: 0, do_confirmed: 0, invoiced: 0, queued_7_11: 0 };
  rows.forEach((row) => {
    if (isBdcShipmentStatus(row.status)) counts[row.status] += 1;
  });
  return counts;
};
